import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Container, Message } from "semantic-ui-react";
import { listUsersOrders } from "../../actions/orderActions";
import Spinner from "../../components/Spinner";
import { Heading1 } from "../../components/value-cssmodules/TextComponent";
import { Order, OrderItem } from "../../types/orderTypes";
import { orderListSelector } from "../../utils/selectors";
import styles from "./PurchaseHistory.module.scss";
import DownloadPatterns from "./DownloadPatterns";
import DateRangePicker from "../../components/DateRange/DateRange";
import { DateRange } from "react-day-picker";
import PrimaryButton from "../../components/Button/PrimaryButton";

type RowTone = "paid" | "cancelled";
type StatusFilter = "all" | "paid" | "cancelled";
type HistoryPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
};

type HistoryRow = {
  key: string;
  orderId: string;
  orderIdShort: string;
  patternId: string;
  productPhoto: string;
  productName: string;
  size: string;
  dateLabel: string;
  sortTimestamp: number;
  price: number;
  paymentType: string;
  statusLabelKey: string;
  statusTone: RowTone;
  statusReason: string | null;
};

type StatusFilterOption = {
  value: StatusFilter;
  label: string;
};

type StatusFilterDropdownProps = {
  value: StatusFilter;
  options: StatusFilterOption[];
  onChange: (nextStatus: StatusFilter) => void;
  ariaLabel: string;
  className?: string;
};

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateMenuPosition = () => {
    if (!buttonRef.current) {
      return;
    }
    const buttonRect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: buttonRect.bottom + 4,
      left: buttonRect.left,
      width: buttonRect.width,
    });
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!dropdownRef.current) {
        return;
      }
      const eventTarget = event.target as Node;
      if (
        !dropdownRef.current.contains(eventTarget) &&
        !menuRef.current?.contains(eventTarget)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuPosition();

    const handleViewportChange = () => {
      updateMenuPosition();
    };
    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const selectedOption =
    options.find((option) => option.value === value) || options[0];

  const menu = isOpen && menuPosition && typeof document !== "undefined"
    ? createPortal(
      <div
        ref={menuRef}
        className={styles.statusFilterMenu}
        role="listbox"
        style={{
          position: "fixed",
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
          zIndex: 2200,
        }}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={option.value === value}
            className={`${styles.statusFilterOption} ${option.value === value ? styles.statusFilterOptionActive : ""
              }`.trim()}
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>,
      document.body
    )
    : null;

  return (
    <div
      ref={dropdownRef}
      className={`${styles.statusFilter} ${className || ""}`.trim()}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.statusFilterButton} ${isOpen ? styles.statusFilterButtonOpen : ""
          }`.trim()}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span>{selectedOption?.label || "-"}</span>
        <span className={styles.statusFilterChevron}>▼</span>
      </button>
      {menu}
    </div>
  );
};

const HISTORY_PAGE_LIMIT = 50;

const formatOrderSize = (sizeValue: string): string => {
  const normalized = String(sizeValue || "").trim().toLowerCase();
  if (!normalized) {
    return "-";
  }
  return normalized === "onesize" ? "ONE SIZE" : normalized.toUpperCase();
};

const makeShortOrderId = (orderId: string): string => {
  if (!orderId) {
    return "-";
  }
  const suffix = orderId.slice(-6).toUpperCase();
  return `#${suffix}`;
};

const getPaymentTypeLabel = (paymentMethod: string, intl: any): string => {
  const method = String(paymentMethod || "").trim().toLowerCase();
  if (method === "stripe") {
    return intl.formatMessage({
      id: "order.history.paymentType.cardStripe",
      defaultMessage: "Card (Stripe)",
    });
  }
  if (method === "paypal") {
    return "PayPal";
  }
  return paymentMethod || "-";
};

const resolveStatus = (order: Order) => {
  const orderStatus = String(order.orderStatus || "")
    .trim()
    .toLowerCase();
  const providerStatus = String(order.paymentResult?.status || "")
    .trim()
    .toLowerCase();
  const reason =
    (typeof order.statusReason === "string" && order.statusReason) ||
    (typeof order.paymentResult?.reason === "string" &&
      order.paymentResult?.reason) ||
    null;
  const successStatuses = ["paid", "completed", "succeeded", "success"];
  if (
    orderStatus === "paid" ||
    order.isPaid ||
    successStatuses.includes(providerStatus)
  ) {
    return {
      labelKey: "order.history.status.paid",
      tone: "paid" as RowTone,
      reason: null,
    };
  }
  return {
    labelKey: "order.history.status.cancelled",
    tone: "cancelled" as RowTone,
    reason:
      reason ||
      (providerStatus
        ? "order.history.reason.paymentFailedFallback"
        : "order.history.reason.paymentUnknownFallback"),
  };
};

const resolveHistoryDate = (
  order: Order
): { label: string; timestamp: number } | null => {
  const candidates = [order.paidAt, order.paymentResult?.update_time];

  for (const value of candidates) {
    const parsed = dayjs(value as string | number | Date);
    if (parsed.isValid()) {
      return {
        label: parsed.format("DD.MM.YYYY"),
        timestamp: parsed.valueOf(),
      };
    }
  }

  return null;
};

const PurchaseHistory = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { orders, loading, error, pagination } = useSelector(
    (state: any) => orderListSelector(state)
  ) as {
    orders: Order[];
    loading?: boolean;
    error?: string;
    pagination?: HistoryPagination | null;
    availableYears?: number[];
  };
  const currentYear = dayjs().year();
  const [range, setRange] = useState<DateRange | undefined>();
  const [appliedRange, setAppliedRange] = useState<DateRange | undefined>();
  const [page, setPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [isYearMenuOpen, setIsYearMenuOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    year: number;
    rangeStartDay?: Date;
    rangeEndDay?: Date;
    statusFilter: StatusFilter;
  }>({
    year: currentYear,
    rangeStartDay: undefined,
    rangeEndDay: undefined,
    statusFilter: "all",
  });
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const yearSelectRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!yearSelectRef.current) {
        return;
      }
      if (!yearSelectRef.current.contains(event.target as Node)) {
        setIsYearMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsYearMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflowX = html.style.overflowX;
    const previousBodyOverflowX = body.style.overflowX;

    html.style.overflowX = "hidden";
    body.style.overflowX = "hidden";

    return () => {
      html.style.overflowX = previousHtmlOverflowX;
      body.style.overflowX = previousBodyOverflowX;
    };
  }, []);

  const canLoadMore = Boolean(pagination?.hasMore);

  useEffect(() => {
    const payload: any = {
      page,
      limit: HISTORY_PAGE_LIMIT,
      status: appliedFilters.statusFilter,
    };

    // only add date if selected
    if (appliedRange?.from && appliedRange?.to) {
      payload.from = appliedRange.from.toISOString();
      payload.to = appliedRange.to.toISOString();
    }

    dispatch(
      listUsersOrders(payload, {
        append: page > 1,
      })
    );
  }, [dispatch, appliedFilters.statusFilter, page, appliedRange]);

  const rows = useMemo(() => {
    const sourceOrders: Order[] = Array.isArray(orders) ? orders : [];

    return sourceOrders
      .flatMap((order: Order) => {
        const orderItems: OrderItem[] = Array.isArray(order.orderItems)
          ? order.orderItems
          : [];
        const historyDate = resolveHistoryDate(order);
        if (!historyDate) {
          return [];
        }
        const status = resolveStatus(order);
        const statusReason =
          typeof status.reason === "string" && status.reason.startsWith("order.")
            ? intl.formatMessage({
              id: status.reason,
              defaultMessage: "Payment has failed",
            })
            : status.reason;

        return orderItems.map((item: OrderItem, index: number): HistoryRow => ({
          key: `${order._id}-${item.id || item._id || index}-${item.selectedSize || ""}`,
          orderId: order._id,
          orderIdShort: makeShortOrderId(order._id),
          patternId: item.id || "",
          productPhoto: item.photo?.thumbnailUrl || item.photo?.url || "/photos/no-photo.jpg",
          productName: item.name || "-",
          size:
            typeof item.selectedSize === "string" && item.selectedSize.trim()
              ? formatOrderSize(item.selectedSize)
              : "-",
          dateLabel: historyDate.label,
          sortTimestamp: historyDate.timestamp,
          price: Number(item.price || 0),
          paymentType: getPaymentTypeLabel(order.paymentMethod, intl),
          statusLabelKey: status.labelKey,
          statusTone: status.tone,
          statusReason,
        }));
      })
      .filter((row: HistoryRow) => {
        if (!range?.from || !range?.to) return true;

        const ts = row.sortTimestamp;

        return (
          ts >= range.from.valueOf() &&
          ts <= range.to.valueOf()
        );
      })
      .filter((row: HistoryRow) => {
        if (appliedFilters.statusFilter === "all") {
          return true;
        }
        return row.statusTone === appliedFilters.statusFilter;
      })
      .sort((a: HistoryRow, b: HistoryRow) => b.sortTimestamp - a.sortTimestamp);
  }, [orders, intl, appliedFilters.statusFilter, range]);




  const handleStatusFilterChange = (nextStatus: StatusFilter) => {
    setStatusFilter(nextStatus);
    setAppliedFilters((prev) => ({
      ...prev,
      statusFilter: nextStatus,
    }));
    setPage(1);
  };


  const handleCopyOrderId = async (orderId: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(orderId);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = orderId;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedOrderId(orderId);
      window.setTimeout(() => setCopiedOrderId(null), 1800);
    } catch (copyError) {
      setCopiedOrderId(null);
    }
  };

  const renderStatusClass = (tone: RowTone): string => {
    return tone === "paid" ? styles.statusPaid : styles.statusCancelled;
  };

  const renderPrice = (price: number): string =>
    new Intl.NumberFormat(intl.locale || "pl", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);

  const statusFilterOptions: StatusFilterOption[] = [
    {
      value: "all",
      label: intl.formatMessage({
        id: "order.history.statusFilter.all",
        defaultMessage: "All",
      }),
    },
    {
      value: "paid",
      label: intl.formatMessage({
        id: "order.history.statusFilter.paid",
        defaultMessage: "Completed",
      }),
    },
    {
      value: "cancelled",
      label: intl.formatMessage({
        id: "order.history.statusFilter.cancelled",
        defaultMessage: "Cancelled",
      }),
    },
  ];

  const handleApply = () => {
    if (!range?.from || !range?.to) return;
    setAppliedRange(range)
  }

  return (
    <Container className={styles.self}>
      <div className={styles.topBar}>
        <Heading1>
          <FormattedMessage
            id="order.history.title"
            defaultMessage="Historia zakupów"
          />
        </Heading1>
        <div className={styles.filtersPanel}>
          <div className={styles.filterGroup}>
            <div
              className={styles.filterContainer}
            >
              {/* Trigger */}
              {orders.length > 0 && (
                <PrimaryButton

                  onClick={() => setIsYearMenuOpen(!isYearMenuOpen)}>
                  {range?.from && range?.to
                    ? `${range.from.toDateString()} → ${range.to.toDateString()}`
                    : intl.formatMessage({
                        id: "order.history.dateRange.select",
                        defaultMessage: "Select date range",
                      })}
                </PrimaryButton>
              )}


              {/* Popup calendar */}
              {isYearMenuOpen && (
                <div
                  className={styles.popupCalender}
                >
                  <DateRangePicker
                    range={range}
                    setRange={setRange}
                  />

                  <div className={styles.calenderFooter}>
                    <button onClick={() => setIsYearMenuOpen(false)} className={styles.calenderButton}>
                      {intl.formatMessage({
                        id: "order.history.action.cancel",
                        defaultMessage: "Cancel",
                      })}
                    </button>
                    <button onClick={handleApply} className={styles.calenderButton}>
                      {intl.formatMessage({
                        id: "order.history.applyFilters",
                        defaultMessage: "Apply",
                      })}
                    </button>
                  </div>
                </div>

              )}
            </div>
          </div>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <Spinner />
      ) : error ? (
        <Message warning>{error}</Message>
      ) : rows.length === 0 ? (
        <Message info>
          <FormattedMessage
            id="order.history.empty"
            defaultMessage="Brak zamówień w wybranym okresie."
          />
        </Message>
      ) : (
        <div className={styles.resultsWrap}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage
                      id="order.history.columns.orderNumber"
                      defaultMessage="Numer zamówienia"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="order.history.columns.product"
                      defaultMessage="Produkt"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="order.history.columns.name"
                      defaultMessage="Nazwa"
                    />
                  </th>
                  <th>
                    <FormattedMessage
                      id="order.history.columns.size"
                      defaultMessage="Rozmiar"
                    />
                  </th>
                  <th>
                    <FormattedMessage id="order.history.columns.date" defaultMessage="Data" />
                  </th>
                  <th>
                    <FormattedMessage id="order.history.columns.price" defaultMessage="Cena" />
                  </th>
                  <th>
                    <FormattedMessage
                      id="order.history.columns.paymentType"
                      defaultMessage="Typ płatności"
                    />
                  </th>
                  <th>
                    <div className={styles.statusHeader}>
                      <FormattedMessage
                        id="order.history.columns.status"
                        defaultMessage="Status"
                      />
                      <StatusFilterDropdown
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        options={statusFilterOptions}
                        ariaLabel={intl.formatMessage({
                          id: "order.history.statusFilter.label",
                          defaultMessage: "Filter by status",
                        })}
                        className={styles.desktopStatusFilterDropdown}
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row: HistoryRow) => (
                  <tr key={row.key}>
                    <td className={styles.orderIdCol}>
                      <div className={styles.orderIdMain}>{row.orderIdShort}</div>
                      <div className={styles.orderActions}>
                        <button
                          type="button"
                          className={styles.copyButton}
                          onClick={() =>
                            setExpandedOrderId(
                              expandedOrderId === row.orderId ? null : row.orderId
                            )
                          }
                        >
                          {expandedOrderId === row.orderId
                            ? intl.formatMessage({
                              id: "order.history.hideId",
                              defaultMessage: "Ukryj ID",
                            })
                            : intl.formatMessage({
                              id: "order.history.showId",
                              defaultMessage: "Pokaż pełne ID",
                            })}
                        </button>
                        <button
                          type="button"
                          className={styles.copyButton}
                          onClick={() => handleCopyOrderId(row.orderId)}
                        >
                          {copiedOrderId === row.orderId
                            ? intl.formatMessage({
                              id: "order.history.copied",
                              defaultMessage: "Skopiowano",
                            })
                            : intl.formatMessage({
                              id: "order.history.copyId",
                              defaultMessage: "Kopiuj ID",
                            })}
                        </button>
                      </div>
                      {expandedOrderId === row.orderId && (
                        <div className={styles.orderIdFull}>{row.orderId}</div>
                      )}
                    </td>
                    <td>
                      {row.patternId ? (
                        <Link to={`/pattern/${row.patternId}`} className={styles.productLink}>
                          <img
                            src={row.productPhoto}
                            alt={row.productName}
                            className={styles.productImage}
                            onError={(event) => {
                              event.currentTarget.src = "/photos/no-photo.jpg";
                            }}
                          />
                        </Link>
                      ) : (
                        <img
                          src={row.productPhoto}
                          alt={row.productName}
                          className={styles.productImage}
                          onError={(event) => {
                            event.currentTarget.src = "/photos/no-photo.jpg";
                          }}
                        />
                      )}
                    </td>
                    <td className={styles.productName}>
                      {row.patternId ? (
                        <Link to={`/pattern/${row.patternId}`} className={styles.nameLink}>
                          {row.productName}
                        </Link>
                      ) : (
                        row.productName
                      )}
                    </td>
                    <td>{row.size}</td>
                    <td>{row.dateLabel}</td>
                    <td>{renderPrice(row.price)}</td>
                    <td>{row.paymentType}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span className={`${styles.statusBadge} ${renderStatusClass(row.statusTone)}`}>
                          <FormattedMessage
                            id={row.statusLabelKey}
                            defaultMessage="Status"
                          />
                        </span>

                        {row.statusTone === "paid" && row.patternId && (
                          <DownloadPatterns
                            patternName={row.productName}
                            patternId={row.patternId} />
                        )}
                      </div>

                      {row.statusReason && (
                        <div className={styles.statusReason}>{row.statusReason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.mobileList}>
            {rows.map((row: HistoryRow) => (
              <article key={`${row.key}-mobile`} className={styles.mobileCard}>
                <div className={styles.mobileCardTop}>
                  <div className={styles.mobileStatusBlock}>
                    <span
                      className={`${styles.statusBadge} ${renderStatusClass(row.statusTone)}`}
                    >
                      <FormattedMessage id={row.statusLabelKey} defaultMessage="Status" />
                    </span>
                    {row.statusReason && (
                      <div className={styles.mobileStatusReason}>{row.statusReason}</div>
                    )}
                  </div>
                  <span className={styles.mobileDate}>{row.dateLabel}</span>
                </div>
                <div className={styles.mobileMain}>
                  {row.patternId ? (
                    <Link to={`/pattern/${row.patternId}`} className={styles.productLink}>
                      <img
                        src={row.productPhoto}
                        alt={row.productName}
                        className={`${styles.productImage} ${styles.mobileProductImage}`}
                        onError={(event) => {
                          event.currentTarget.src = "/photos/no-photo.jpg";
                        }}
                      />
                    </Link>
                  ) : (
                    <img
                      src={row.productPhoto}
                      alt={row.productName}
                      className={`${styles.productImage} ${styles.mobileProductImage}`}
                      onError={(event) => {
                        event.currentTarget.src = "/photos/no-photo.jpg";
                      }}
                    />
                  )}
                  <div className={styles.mobileMainContent}>
                    <div className={styles.mobileName}>
                      {row.patternId ? (
                        <Link to={`/pattern/${row.patternId}`} className={styles.nameLink}>
                          {row.productName}
                        </Link>
                      ) : (
                        row.productName
                      )}
                    </div>
                    <div className={styles.mobileMetaRow}>
                      <span className={styles.mobileMetaLabel}>
                        {intl.formatMessage({
                          id: "order.history.columns.price",
                          defaultMessage: "Cena",
                        })}
                      </span>
                      <span>{renderPrice(row.price)}</span>
                    </div>
                    <div className={styles.mobileMetaRow}>
                      <span className={styles.mobileMetaLabel}>
                        {intl.formatMessage({
                          id: "order.history.columns.size",
                          defaultMessage: "Rozmiar",
                        })}
                      </span>
                      <span>{row.size}</span>
                    </div>
                    <div className={styles.mobileMetaRow}>
                      <span className={styles.mobileMetaLabel}>
                        {intl.formatMessage({
                          id: "order.history.columns.paymentType",
                          defaultMessage: "Typ płatności",
                        })}
                      </span>
                      <span>{row.paymentType}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.mobileOrderSection}>
                  <div className={styles.orderIdMain}>{row.orderIdShort}</div>
                  <div className={styles.orderActions}>
                    <button
                      type="button"
                      className={styles.copyButton}
                      onClick={() =>
                        setExpandedOrderId(
                          expandedOrderId === row.orderId ? null : row.orderId
                        )
                      }
                    >
                      {expandedOrderId === row.orderId
                        ? intl.formatMessage({
                          id: "order.history.hideId",
                          defaultMessage: "Ukryj ID",
                        })
                        : intl.formatMessage({
                          id: "order.history.showId",
                          defaultMessage: "Pokaż pełne ID",
                        })}
                    </button>
                    <button
                      type="button"
                      className={styles.copyButton}
                      onClick={() => handleCopyOrderId(row.orderId)}
                    >
                      {copiedOrderId === row.orderId
                        ? intl.formatMessage({
                          id: "order.history.copied",
                          defaultMessage: "Skopiowano",
                        })
                        : intl.formatMessage({
                          id: "order.history.copyId",
                          defaultMessage: "Kopiuj ID",
                        })}
                    </button>
                  </div>
                  {expandedOrderId === row.orderId && (
                    <div className={styles.orderIdFull}>{row.orderId}</div>
                  )}
                </div>
              </article>
            ))}
          </div>
          {canLoadMore && (
            <div className={styles.loadMoreWrap}>
              <button
                type="button"
                className={styles.loadMoreButton}
                onClick={() => setPage((prevPage) => prevPage + 1)}
                disabled={Boolean(loading)}
              >
                {loading
                  ? intl.formatMessage({
                    id: "order.history.loadMore.loading",
                    defaultMessage: "Ładowanie...",
                  })
                  : intl.formatMessage({
                    id: "order.history.loadMore.default",
                    defaultMessage: "Pokaż więcej",
                  })}
              </button>
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default PurchaseHistory;
